import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import {
  PrivacyTip,
  DataUsage,
  ExpandMore,
  Folder,
  LocationOn,
  FilePresent,
  Settings,
} from '@mui/icons-material';

const Privacy = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  const handleAnalyticsToggle = () => {
    setAnalyticsEnabled(!analyticsEnabled);
  };

  // Updated: simplified, contains NO technical backend info
  const dataCollectionDetails = [
    { 
      type: 'Account Information', 
      description: 'Basic profile details such as your name, email, and assigned work location.',
      icon: <LocationOn />
    },
    { 
      type: 'File & Folder Metadata', 
      description: 'Folder names, file names, and timestamps for organization and management.',
      icon: <Folder />
    },
    { 
      type: 'Usage Insights', 
      description: 'General actions such as page visits and file operations to help improve performance.',
      icon: <DataUsage />
    },
    { 
      type: 'Support Requests', 
      description: 'Messages or queries submitted through the support system.',
      icon: <FilePresent />
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Privacy & Security
      </Typography>

      <Grid container spacing={3}>
        
        {/* Privacy Controls */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Settings color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Privacy Controls</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={analyticsEnabled}
                    onChange={handleAnalyticsToggle}
                    color="primary"
                  />
                }
                label="Analytics & Performance Insights"
                sx={{ display: 'block', mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary">
                Allow collection of anonymous usage insights to help improve
                overall performance and user experience.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* What Data We Collect */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PrivacyTip color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Data We Collect</Typography>
              </Box>

              <List>
                {dataCollectionDetails.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <Box display="flex" alignItems="center">
                        {item.icon}
                        <ListItemText
                          primary={item.type}
                          secondary={item.description}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </ListItem>
                    {index < dataCollectionDetails.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Info */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Privacy Information
              </Typography>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Data Retention</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    Your account and folder metadata remain available as long as your
                    account is active. Files remain stored until you choose to delete them.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Your Rights</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    You can request updates to your account information or ask for
                    removal of your files or folders at any time through the support team.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Security Commitment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    We prioritize safety, privacy, and reliability across our file
                    management system. All stored data is handled with care and
                    protected using industry-standard security practices.
                  </Typography>
                </AccordionDetails>
              </Accordion>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Simple Footer */}
      <Box sx={{ mt: 4, pt: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          © 2025 IMS Private Limited — All Rights Reserved.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your privacy and data security are our highest priority.
        </Typography>
      </Box>
    </Container>
  );
};

export default Privacy;
